import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Component, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { HasPermissionPipe } from 'src/app/core/pipes/has-permission.pipe';
import { SUPER_ADMIN_PERMISSION_SLUGS } from 'src/app/core/constants/super-admin-permission-slugs';
import { PageSurfaceHeaderComponent } from 'src/app/shared/page-surface-header/page-surface-header.component';
import { IconModule } from 'src/app/shared/icon/icon.module';
import { StatusStripComponent } from './status-strip.component';
import { CheckIconComponent } from './check-icon.component';
import {
  HealthHistoryPoint,
  HealthStatus,
  HealthTarget,
  PlatformHealth,
  PlatformHealthService,
  UptimeRow,
} from 'src/app/core/services/platform-health.service';

const REFRESH_MS = 60_000;
const LOAD_ERROR = 'Could not load platform health';
const STRIP_DAYS = 90;
type UptimeWindow = '24h' | '7d' | '30d' | '90d';
const UPTIME_WINDOWS: UptimeWindow[] = ['24h', '7d', '30d', '90d'];
const SEVERITY_ORDER: Record<HealthStatus, number> = { unknown: 0, ok: 1, degraded: 2, down: 3 };

const DOT_CLASS_BY_STATUS: Record<HealthStatus, string> = {
  ok: 'bg-emerald-500',
  degraded: 'bg-amber-500',
  down: 'bg-red-500',
  unknown: 'bg-gray-400',
};

/** Labelled status pill; colour and word together so the state reads without a legend. */
const PILL_CLASS_BY_STATUS: Record<HealthStatus, string> = {
  ok: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-300',
  degraded: 'bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-300',
  down: 'bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-300',
  unknown: 'bg-gray-100 text-gray-500 dark:bg-white/5 dark:text-gray-400',
};

/** Per-group counts shown in the summary strip. */
export interface StatusCounts {
  ok: number;
  degraded: number;
  down: number;
  unknown: number;
}

const EMPTY_COUNTS: StatusCounts = { ok: 0, degraded: 0, down: 0, unknown: 0 };

function countBy(targets: HealthTarget[]): StatusCounts {
  const counts = { ...EMPTY_COUNTS };
  for (const t of targets) counts[t.status] += 1;
  return counts;
}

/** Worst status across a group: what the summary badge and the page title colour show. */
function worstOf(targets: HealthTarget[]): HealthStatus {
  if (targets.some((t) => t.status === 'down')) return 'down';
  if (targets.some((t) => t.status === 'degraded')) return 'degraded';
  if (targets.some((t) => t.status === 'ok')) return 'ok';
  return 'unknown';
}

@Component({
  selector: 'app-platform-health',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    IconModule,
    HasPermissionPipe,
    PageSurfaceHeaderComponent,
    StatusStripComponent,
    CheckIconComponent,
  ],
  templateUrl: './platform-health.component.html',
})
export class PlatformHealthComponent implements OnInit, OnDestroy {
  private readonly platformHealthService = inject(PlatformHealthService);
  private refreshHandle?: ReturnType<typeof setInterval>;

  readonly P = SUPER_ADMIN_PERMISSION_SLUGS;
  readonly checkupEnabled = signal(true);
  readonly checkupSaving = signal(false);
  readonly checkupError = signal<string | null>(null);

  readonly windows = UPTIME_WINDOWS;
  readonly stripDays = STRIP_DAYS;

  readonly health = signal<PlatformHealth | null>(null);
  readonly uptimeByKey = signal<Map<string, UptimeRow>>(new Map());
  readonly historyByKey = signal<Map<string, HealthHistoryPoint[]>>(new Map());
  readonly window = signal<UptimeWindow>('24h');
  readonly expandedKey = signal<string | null>(null);
  /** Set when the current-state call fails (401/500), so the page shows a line instead of staying blank. */
  readonly loadError = signal<string | null>(null);
  /** Tenant list controls — the service list is short and fixed, tenants are not. */
  tenantQuery = '';
  problemsOnly = false;

  get serviceCounts(): StatusCounts {
    return countBy(this.health()?.services ?? []);
  }

  get tenantCounts(): StatusCounts {
    return countBy(this.health()?.tenants ?? []);
  }

  get overallStatus(): HealthStatus {
    const h = this.health();
    if (!h) return 'unknown';
    return worstOf([...h.services, ...h.tenants]);
  }

  get overallLabel(): string {
    if (!this.checkupEnabled()) {
      return 'Monitoring paused';
    }
    const labels: Record<HealthStatus, string> = {
      ok: 'All systems operational',
      degraded: 'Degraded',
      down: 'Outage',
      unknown: 'No data yet',
    };
    return labels[this.overallStatus];
  }

  /** Tenants after the search box and the problems-only toggle; unhealthy first. */
  get visibleTenants(): HealthTarget[] {
    const q = this.tenantQuery.trim().toLowerCase();
    return (this.health()?.tenants ?? [])
      .filter((t) => !q || t.target_key.toLowerCase().includes(q))
      .filter((t) => !this.problemsOnly || t.status !== 'ok')
      .sort((a, b) => SEVERITY_ORDER[b.status] - SEVERITY_ORDER[a.status] || a.target_key.localeCompare(b.target_key));
  }

  ngOnInit(): void {
    this.platformHealthService.getCheckupSettings().subscribe({
      next: (res) => {
        const enabled = res.data?.checkup_enabled ?? true;
        this.checkupEnabled.set(enabled);
        this.applyMonitoringState(enabled, true);
      },
      error: () => {
        this.checkupEnabled.set(true);
        this.applyMonitoringState(true, true);
      },
    });
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  /** Forces a fresh probe of every target instead of reading the latest stored samples. */
  probeNow(): void {
    if (!this.checkupEnabled()) {
      return;
    }
    this.loadCurrent(true, true);
  }

  onCheckupToggle(enabled: boolean): void {
    if (this.checkupSaving() || enabled === this.checkupEnabled()) {
      return;
    }
    const previous = this.checkupEnabled();
    this.checkupEnabled.set(enabled);
    this.checkupSaving.set(true);
    this.checkupError.set(null);
    this.platformHealthService.updateCheckupSettings(enabled).subscribe({
      next: (res) => {
        const on = res.data?.checkup_enabled ?? enabled;
        this.checkupEnabled.set(on);
        this.checkupSaving.set(false);
        this.applyMonitoringState(on, on);
      },
      error: () => {
        this.checkupEnabled.set(previous);
        this.checkupSaving.set(false);
        this.checkupError.set('Could not update monitoring setting.');
      },
    });
  }

  onWindowChange(window: UptimeWindow): void {
    this.window.set(window);
    if (this.checkupEnabled()) {
      this.loadUptime();
    }
  }

  /** When monitoring is off: no GET /platform/health, uptime, history, or 60s polling. */
  private applyMonitoringState(enabled: boolean, initialLoad: boolean): void {
    if (!enabled) {
      this.stopPolling();
      this.loadError.set(null);
      return;
    }
    if (initialLoad) {
      this.loadCurrent(false, true);
      this.loadUptime();
    }
    this.startPolling();
  }

  private startPolling(): void {
    this.stopPolling();
    this.refreshHandle = setInterval(() => this.loadCurrent(false, false), REFRESH_MS);
  }

  private stopPolling(): void {
    if (this.refreshHandle != null) {
      clearInterval(this.refreshHandle);
      this.refreshHandle = undefined;
    }
  }

  toggle(key: string): void {
    this.expandedKey.set(this.expandedKey() === key ? null : key);
  }

  isExpanded(key: string): boolean {
    return this.expandedKey() === key;
  }

  targetKey(target: HealthTarget): string {
    return `${target.target_kind}:${target.target_key}`;
  }

  checksOf(target: HealthTarget): { name: string; status: string; required: boolean; latency_ms: number; detail?: string }[] {
    return Object.entries(target.checks).map(([name, check]) => ({ name, ...check }));
  }

  dotClass(status: HealthStatus): string {
    return DOT_CLASS_BY_STATUS[status];
  }

  pillClass(status: HealthStatus): string {
    return PILL_CLASS_BY_STATUS[status];
  }

  failingChecks(target: HealthTarget): string[] {
    return Object.entries(target.checks)
      .filter(([, check]) => check.status === 'fail')
      .map(([name]) => name);
  }

  /** Uptime is coloured like a status: <99 amber, <95 red. */
  uptimeClass(key: string): string {
    const row = this.uptimeByKey().get(key);
    if (row?.uptime == null) return 'text-gray-400';
    if (row.uptime < 0.95) return 'text-red-600';
    if (row.uptime < 0.99) return 'text-amber-600';
    return 'text-emerald-600';
  }

  uptimeText(key: string): string {
    const row = this.uptimeByKey().get(key);
    if (row?.uptime == null) return 'no data';
    return `${(row.uptime * 100).toFixed(1)}%`;
  }

  historyOf(key: string): HealthHistoryPoint[] {
    return this.historyByKey().get(key) ?? [];
  }

  private loadCurrent(live: boolean, withHistory: boolean): void {
    this.platformHealthService.current(live).subscribe({
      next: (res) => {
        this.health.set(res.data);
        if (res.data?.checkup_enabled != null) {
          this.checkupEnabled.set(res.data.checkup_enabled);
        }
        this.loadError.set(null);
        if (withHistory) this.loadHistoryFor(res.data);
      },
      error: () => this.loadError.set(LOAD_ERROR),
    });
  }

  private loadUptime(): void {
    // A failed uptime call only leaves the percentages at "no data"; the rows still render.
    this.platformHealthService.uptime(this.window()).subscribe({
      next: (res) => {
        const rows = res.data?.rows ?? [];
        this.uptimeByKey.set(new Map(rows.map((row) => [`${row.target_kind}:${row.target_key}`, row])));
      },
      error: () => this.uptimeByKey.set(new Map()),
    });
  }

  // One 1d-bucketed history call per target so the strip can show the full retention window.
  private loadHistoryFor(health: PlatformHealth | null): void {
    const targets = [...(health?.services ?? []), ...(health?.tenants ?? [])];
    const to = new Date();
    const from = new Date(to.getTime() - (this.stripDays - 1) * 24 * 60 * 60 * 1000);
    for (const target of targets) {
      const key = this.targetKey(target);
      this.platformHealthService
        .history(key, from.toISOString(), to.toISOString(), '1d')
        .subscribe({
          next: (res) => this.historyByKey.update((map) => new Map(map).set(key, res.data?.points ?? [])),
          // A failed history call leaves only this target's strip grey.
          error: () => this.historyByKey.update((map) => new Map(map).set(key, [])),
        });
    }
  }
}
